import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { Form } from '@/common/components/Form/Form';
import {
  TChoseNameOnCardStepSchema,
  choseNameOnCardStepSchema,
} from '@/common/containers/Flow/Profile/Card/CardRequest/Steps/ChoseNameOnCard/ChoseNameOnCardStep.schema';
import { useValidateCardName } from '@/common/integrations/menhir/employee/card/card.hooks';
import { FlowLayout } from '@/common/layouts/FlowLayout/FlowLayout';
import { useFlowLayout } from '@/common/layouts/FlowLayout/FlowLayout.context';
import { zodResolver } from '@hookform/resolvers/zod';

export interface ICardNameProps {
  cardHolderName?: string;
  setCardHolderName: (name: string) => void;
  isExpiring?: boolean;
}

const MAX_NAME_SIZE = 19;

export const ChoseNameOnCardStep = ({
  cardHolderName,
  setCardHolderName,
}: ICardNameProps) => {
  const { t } = useTranslation();

  const { goNext } = useFlowLayout();
  const { mutateAsync: validateCardName } = useValidateCardName();

  const methods = useForm<TChoseNameOnCardStepSchema>({
    mode: 'onTouched',
    defaultValues: {
      name: cardHolderName ? cardHolderName.toLocaleUpperCase() : '',
    },
    resolver: zodResolver(choseNameOnCardStepSchema),
  });

  const onSubmit = async ({ name }: { name: string }) => {
    try {
      await validateCardName({ cardHolderName: name });
      setCardHolderName(name);
      goNext();
    } catch (error) {
      methods.setError('name', {
        message: 'errors.api.holder_name',
      });
    }
  };

  return (
    <FlowLayout.Content
      primaryAction={{
        children: t('general.navigation.next'),
        onPress: methods.handleSubmit(onSubmit),
        loading: methods.formState.isSubmitting,
      }}
    >
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <ControlledTextField
            name="name"
            label={t('onboarding.italy.card.name_input')}
            inputFormatter={(val) => val?.toUpperCase() ?? ''}
            hint={t('onboarding.italy.card.name_hint')}
            maxLength={MAX_NAME_SIZE}
          />
        </Form>
      </FormProvider>
    </FlowLayout.Content>
  );
};
